import * as THREE from 'three';
import { MAX_JET_DISTANCE } from './constants';
import { JetStreamConfig } from './types';

/**
 * JetStream manages a single jet particle system (north or south).
 *
 * Each particle stores its own emission direction at birth. As the star spins
 * and the magnetic axis precesses, new particles get slightly different directions,
 * naturally creating a spiral trail. Particles also have helical motion around
 * their emission axis for a dense funnel appearance.
 */
export class JetStream {
  public points: THREE.Points;

  private geometry: THREE.BufferGeometry;
  private positions: Float32Array;
  private velocities: Float32Array;
  private ages: Float32Array;
  private colors: Float32Array;
  private opacities: Float32Array;
  private sizes: Float32Array;
  private emissionColors: Float32Array;

  // Per-particle emission direction (frozen at birth) — creates spiral
  private emitDirs: Float32Array;
  // Per-particle emission origin (frozen at birth)
  private emitOrigins: Float32Array;
  // Per-particle perpendicular basis vectors for helix
  private perpAs: Float32Array;
  private perpBs: Float32Array;

  // Per-particle helical motion parameters
  private helixPhases: Float32Array;
  private helixRadii: Float32Array;
  private helixSpeeds: Float32Array;

  private maxAge: number;
  private activeCount: number;
  private targetCount: number;
  private maxParticles: number;

  private speed: number;
  private accentColor: THREE.Color;
  private coneHalfAngle: number;
  private direction: 1 | -1;

  constructor(direction: 1 | -1, config: JetStreamConfig) {
    this.direction = direction;
    this.speed = config.speed;
    this.accentColor = config.accentColor.clone();
    this.coneHalfAngle = THREE.MathUtils.degToRad(config.coneHalfAngle);
    this.maxAge = MAX_JET_DISTANCE / this.speed;
    this.targetCount = config.particleCount;
    this.activeCount = 0;
    this.maxParticles = 10000;

    // Initialize typed arrays
    this.positions = new Float32Array(this.maxParticles * 3);
    this.velocities = new Float32Array(this.maxParticles * 3);
    this.ages = new Float32Array(this.maxParticles);
    this.colors = new Float32Array(this.maxParticles * 3);
    this.opacities = new Float32Array(this.maxParticles);
    this.sizes = new Float32Array(this.maxParticles);
    this.emissionColors = new Float32Array(this.maxParticles * 3);
    this.emitDirs = new Float32Array(this.maxParticles * 3);
    this.emitOrigins = new Float32Array(this.maxParticles * 3);
    this.perpAs = new Float32Array(this.maxParticles * 3);
    this.perpBs = new Float32Array(this.maxParticles * 3);
    this.helixPhases = new Float32Array(this.maxParticles);
    this.helixRadii = new Float32Array(this.maxParticles);
    this.helixSpeeds = new Float32Array(this.maxParticles);

    for (let i = 0; i < this.maxParticles; i++) {
      this.ages[i] = this.maxAge;
    }

    // Create BufferGeometry
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    this.geometry.setAttribute('opacity', new THREE.BufferAttribute(this.opacities, 1));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(this.sizes, 1));
    this.geometry.setDrawRange(0, this.targetCount);

    const material = new THREE.ShaderMaterial({
      uniforms: {},
      vertexShader: `
        attribute float size;
        attribute float opacity;
        varying vec3 vColor;
        varying float vOpacity;
        void main() {
          vColor = color;
          vOpacity = opacity;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (200.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        varying float vOpacity;
        void main() {
          float dist = length(gl_PointCoord - vec2(0.5));
          if (dist > 0.5) discard;
          float alpha = vOpacity * (1.0 - dist * 2.0);
          alpha = alpha * alpha;
          gl_FragColor = vec4(vColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: true,
    });

    this.points = new THREE.Points(this.geometry, material);
    this.points.frustumCulled = false;
  }

  /**
   * Update all particles. Each particle travels along its OWN emission direction
   * (frozen at birth), so as the star rotates and the magnetic axis precesses,
   * new particles go in different directions — creating the spiral trail.
   */
  update(deltaTime: number, emissionOrigin: THREE.Vector3, emissionDirection: THREE.Vector3): void {
    const posAttr = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    const colorAttr = this.geometry.getAttribute('color') as THREE.BufferAttribute;
    const opacityAttr = this.geometry.getAttribute('opacity') as THREE.BufferAttribute;
    const sizeAttr = this.geometry.getAttribute('size') as THREE.BufferAttribute;

    for (let i = 0; i < this.targetCount; i++) {
      this.ages[i] += deltaTime;

      if (this.ages[i] >= this.maxAge) {
        this.recycleParticle(i, emissionOrigin, emissionDirection);
      }

      const i3 = i * 3;
      const age = this.ages[i];
      const travelFraction = age / this.maxAge;
      const axialDistance = travelFraction * MAX_JET_DISTANCE;

      // Use THIS particle's frozen emission direction and basis
      const dirX = this.emitDirs[i3];
      const dirY = this.emitDirs[i3 + 1];
      const dirZ = this.emitDirs[i3 + 2];
      const origX = this.emitOrigins[i3];
      const origY = this.emitOrigins[i3 + 1];
      const origZ = this.emitOrigins[i3 + 2];
      const paX = this.perpAs[i3], paY = this.perpAs[i3+1], paZ = this.perpAs[i3+2];
      const pbX = this.perpBs[i3], pbY = this.perpBs[i3+1], pbZ = this.perpBs[i3+2];

      // Funnel radius: narrow at star, widening outward
      const funnelRadius = this.helixRadii[i] * (0.05 + travelFraction * 0.95);
      // Helical phase
      const phase = this.helixPhases[i] + this.helixSpeeds[i] * age;
      const radialX = Math.cos(phase) * funnelRadius;
      const radialY = Math.sin(phase) * funnelRadius;

      // Position = origin + axial along frozen dir + radial helix
      this.positions[i3] = origX + dirX * axialDistance + paX * radialX + pbX * radialY;
      this.positions[i3+1] = origY + dirY * axialDistance + paY * radialX + pbY * radialY;
      this.positions[i3+2] = origZ + dirZ * axialDistance + paZ * radialX + pbZ * radialY;

      // Store velocity for compatibility
      this.velocities[i3] = dirX * this.speed;
      this.velocities[i3+1] = dirY * this.speed;
      this.velocities[i3+2] = dirZ * this.speed;

      // Opacity: quadratic fade
      const opacity = Math.max(0, 1.0 - travelFraction * travelFraction);
      this.opacities[i] = opacity;

      // Size: larger near star
      this.sizes[i] = 0.15 * (1.0 - travelFraction * 0.6);

      // Color gradient: white → accent over first 20%
      const emR = this.emissionColors[i3];
      const emG = this.emissionColors[i3+1];
      const emB = this.emissionColors[i3+2];

      if (travelFraction <= 0.2) {
        const t = travelFraction / 0.2;
        this.colors[i3] = (1.0 + (emR - 1.0) * t) * opacity;
        this.colors[i3+1] = (1.0 + (emG - 1.0) * t) * opacity;
        this.colors[i3+2] = (1.0 + (emB - 1.0) * t) * opacity;
      } else {
        this.colors[i3] = emR * opacity;
        this.colors[i3+1] = emG * opacity;
        this.colors[i3+2] = emB * opacity;
      }
    }

    posAttr.needsUpdate = true;
    colorAttr.needsUpdate = true;
    opacityAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;
  }

  setTargetCount(count: number): void {
    this.targetCount = Math.min(count, this.maxParticles);
    this.geometry.setDrawRange(0, this.targetCount);
    for (let i = this.activeCount; i < this.targetCount; i++) {
      this.ages[i] = this.maxAge;
    }
    this.activeCount = this.targetCount;
  }

  setSpeed(speed: number): void {
    this.speed = speed;
    this.maxAge = MAX_JET_DISTANCE / this.speed;
  }

  setAccentColor(color: THREE.Color): void {
    this.accentColor.copy(color);
  }

  getMaxAge(): number { return this.maxAge; }
  getSpeed(): number { return this.speed; }
  getAccentColor(): THREE.Color { return this.accentColor; }
  getAges(): Float32Array { return this.ages; }
  getVelocities(): Float32Array { return this.velocities; }
  getPositions(): Float32Array { return this.positions; }
  getColors(): Float32Array { return this.colors; }
  getOpacities(): Float32Array { return this.opacities; }
  getEmissionColors(): Float32Array { return this.emissionColors; }
  getTargetCount(): number { return this.targetCount; }

  /**
   * Recycle a particle: freeze the CURRENT emission direction and origin so this
   * particle travels outward along the direction the magnetic axis is pointing NOW.
   * As the star spins, successive recycled particles get different directions → spiral.
   */
  private recycleParticle(index: number, origin: THREE.Vector3, direction: THREE.Vector3): void {
    const i3 = index * 3;

    // Freeze emission origin and direction for this particle's lifetime
    this.emitOrigins[i3] = origin.x;
    this.emitOrigins[i3+1] = origin.y;
    this.emitOrigins[i3+2] = origin.z;
    this.emitDirs[i3] = direction.x;
    this.emitDirs[i3+1] = direction.y;
    this.emitDirs[i3+2] = direction.z;

    // Compute perpendicular basis for this particle's helix
    const perp = new THREE.Vector3();
    if (Math.abs(direction.x) < 0.9) {
      perp.crossVectors(direction, new THREE.Vector3(1, 0, 0)).normalize();
    } else {
      perp.crossVectors(direction, new THREE.Vector3(0, 1, 0)).normalize();
    }
    const perp2 = new THREE.Vector3().crossVectors(direction, perp).normalize();
    this.perpAs[i3] = perp.x; this.perpAs[i3+1] = perp.y; this.perpAs[i3+2] = perp.z;
    this.perpBs[i3] = perp2.x; this.perpBs[i3+1] = perp2.y; this.perpBs[i3+2] = perp2.z;

    // Reset position to origin
    this.positions[i3] = origin.x;
    this.positions[i3+1] = origin.y;
    this.positions[i3+2] = origin.z;

    // Small age stagger for uniform fill
    this.ages[index] = Math.random() * this.maxAge * 0.05;

    // Helical parameters
    this.helixPhases[index] = Math.random() * Math.PI * 2;
    const radiusRandom = Math.pow(Math.random(), 0.5);
    this.helixRadii[index] = radiusRandom * Math.tan(this.coneHalfAngle) * MAX_JET_DISTANCE;
    this.helixSpeeds[index] = (3.0 + Math.random() * 8.0) * (1.0 - radiusRandom * 0.5);

    // Velocity for compatibility
    this.velocities[i3] = direction.x * this.speed;
    this.velocities[i3+1] = direction.y * this.speed;
    this.velocities[i3+2] = direction.z * this.speed;

    // Accent color at emission
    this.emissionColors[i3] = this.accentColor.r;
    this.emissionColors[i3+1] = this.accentColor.g;
    this.emissionColors[i3+2] = this.accentColor.b;
  }
}
